import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { FoljaForma, FoljaPeremri, FoljaKoha, FoljaNumri, FoljaVeta, FoljaMenyra } from '@core/enums/folja.enum';
import { FoljaKushtore } from '@core/globals/folja/folja-kushtore.constants';
import { FoljaConstants } from '@core/globals/folja/folja.constants';
import { FoljaKushtoreForCreationDTO } from '@core/interfaces/dto/folja-kushtore-for-creation-dto.interface';
import { FoljaBaza } from '@core/interfaces/models/folja-baza.interface';
import { FoljaKushtoreFormulaService } from './folja-kushtore-formula.service';

@Injectable({
  providedIn: 'root',
})
export class FoljaKushtoreService {
  constructor(private foljaKushtoreFormulaService: FoljaKushtoreFormulaService, private fb: UntypedFormBuilder) { }

  insertForms(foljaBaza: FoljaBaza, form: UntypedFormGroup) {
    let formArrays = ['foljaBaza.foljaKushtoreInitial', 'foljaBaza.foljaKushtore'];
    for (let i = 0; i < formArrays.length; i++) {
      const formArray = form.get(formArrays[i]) as UntypedFormArray;
      this.generateKushtoreForms(formArray);
      foljaBaza.foljaKushtore?.forEach((item) => {
        let index = formArray.controls.findIndex(
          (c) =>
            c.get('kohaId')?.value == item.kohaId &&
            c.get('formaId')?.value == item.formaId &&
            c.get('vetaId')?.value == item.vetaId &&
            c.get('numriId')?.value == item.numriId &&
            c.get('pershkrimi')?.value == '' &&
            c.get('renditja')?.value == item.renditja
        );
        if (index != -1) {
          formArray.controls[index].get('pershkrimi')?.setValue(item.pershkrimi);
        } else {
          this.addNewLakimi(formArray, item.kohaId, item.formaId, item.vetaId, item.numriId, item.pershkrimi, item.renditja);
        }
      });
    }
  }

  generateForms(formArray: UntypedFormArray) {
    this.generateKushtoreForms(formArray);
  }

  generateFormula(baza: string, foljaBaza: FoljaBaza, form: UntypedFormGroup) {
    this.foljaKushtoreFormulaService.generateFormula(baza, foljaBaza, form.get('foljaBaza.foljaKushtore') as UntypedFormArray);
  }

  restoreFoljaForms(form: UntypedFormGroup, forma: FoljaForma, numrat?: FoljaNumri[], vetat?: FoljaVeta[]) {
    const numratC = FoljaConstants.FoljaNumri.filter((fn) => !numrat || numrat.includes(fn.id));
    const vetatC = FoljaConstants.FoljaVeta.filter((fv) => !vetat || vetat.includes(fv.id));

    Object.keys((form.get('foljaBaza') as UntypedFormGroup).controls)
      .filter((c) => c.startsWith('folja') && c.includes('Kushtore'))
      .forEach((field) => {
        const formArray = <UntypedFormArray>form.get('foljaBaza')?.get(field);

        if (
          !formArray.controls.some((c) => {
            return (
              c.get('formaId')?.value == forma && (!numrat || numrat.includes(c.get('numriId')?.value)) && (!vetat || vetat.includes(c.get('vetaId')?.value))
            );
          })
        ) {
          for (const fk of FoljaKushtore.FoljaKushtoreKoha) {
            for (const fn of numratC) {
              for (const fv of vetatC) {
                const foljaForm = this.fb.group({
                  kohaId: fk.id,
                  formaId: forma.valueOf(),
                  vetaId: fv.id,
                  numriId: fn.id,
                  formaFjalesId: null,
                  pershkrimi: '',
                  renditja: 1
                });
                formArray.push(foljaForm);
              }
            }
          }
        }
      });
  }

  private generateKushtoreForms(formArray: UntypedFormArray) {
    while (formArray.length > 0) {
      formArray.removeAt(0);
    }

    for (const fk of FoljaKushtore.FoljaKushtoreKoha) {
      for (const ff of FoljaConstants.FoljaForma) {
        for (const fv of FoljaConstants.FoljaVeta) {
          for (const fn of FoljaConstants.FoljaNumri) {
            const foljaForm = this.fb.group({
              kohaId: fk.id,
              formaId: ff.id,
              vetaId: fv.id,
              numriId: fn.id,
              pershkrimi: '',
              renditja: 1
            });

            formArray.push(foljaForm);
          }
        }
      }
    }
  }

  addNewLakimi(formArray: UntypedFormArray, koha: FoljaKoha, forma: FoljaForma, veta: FoljaVeta, numri: FoljaNumri, pershkrimi?: string, renditja?: number) {
    const renditjet = formArray.value.filter((c: any) => c.kohaId == koha && c.formaId == forma && c.vetaId == veta && c.numriId == numri).map((c: any) => c.renditja);
    const prevIndex = Math.max(...(renditjet.length > 0 ? renditjet : [0]));
    const foljaForm = this.fb.group({
      kohaId: koha,
      formaId: forma,
      vetaId: veta,
      numriId: numri,
      pershkrimi: pershkrimi,
      renditja: renditja ?? prevIndex + 1
    });
    formArray.push(foljaForm);
  }

  getCreationDTO(form: UntypedFormGroup) {
    const creationDTO: FoljaKushtoreForCreationDTO[] = [];

    let formArray: UntypedFormArray;

    formArray = form.get('foljaBaza.foljaKushtore') as UntypedFormArray;

    const veproreParam = form.get('foljaBaza.veproreParam')?.value;
    const joveproreParam = form.get('foljaBaza.joveproreParam')?.value;
    const veproreNjejesParam = form.get('foljaBaza.veproreNjejesParam')?.value;
    const veproreShumesParam = form.get('foljaBaza.veproreShumesParam')?.value;
    const joveproreNjejesParam = form.get('foljaBaza.joveproreNjejesParam')?.value;
    const joveproreShumesParam = form.get('foljaBaza.joveproreShumesParam')?.value;

    let formArrayValue = formArray.getRawValue();

    if (!veproreParam) {
      formArrayValue = formArrayValue.filter((f: any) => f.formaId != FoljaForma.Veprore);
    } else {
      if (veproreNjejesParam) {
        formArrayValue = formArrayValue.filter(
          (f: any) =>
            f.formaId != FoljaForma.Veprore ||
            (f.numriId == FoljaNumri.Njejes && f.vetaId == FoljaVeta.Etrete) ||
            (veproreShumesParam && f.numriId == FoljaNumri.Shumes)
        );
      }
      if (veproreShumesParam) {
        formArrayValue = formArrayValue.filter(
          (f: any) =>
            f.formaId != FoljaForma.Veprore ||
            (f.numriId == FoljaNumri.Shumes && f.vetaId == FoljaVeta.Etrete) ||
            (veproreNjejesParam && f.numriId == FoljaNumri.Njejes)
        );
      }
    }
    if (!joveproreParam) {
      formArrayValue = formArrayValue.filter((f: any) => f.formaId != FoljaForma.JoVeprore);
    } else {
      if (joveproreNjejesParam) {
        formArrayValue = formArrayValue.filter(
          (f: any) =>
            f.formaId != FoljaForma.JoVeprore ||
            (f.numriId == FoljaNumri.Njejes && f.vetaId == FoljaVeta.Etrete) ||
            (joveproreShumesParam && f.numriId == FoljaNumri.Shumes)
        );
      }
      if (joveproreShumesParam) {
        formArrayValue = formArrayValue.filter(
          (f: any) =>
            f.formaId != FoljaForma.JoVeprore ||
            (f.numriId == FoljaNumri.Shumes && f.vetaId == FoljaVeta.Etrete) ||
            (joveproreNjejesParam && f.numriId == FoljaNumri.Njejes)
        );
      }
    }

    formArrayValue.forEach((e: any) => {
      creationDTO.push({
        kohaId: e.kohaId,
        formaId: e.formaId,
        vetaId: e.vetaId,
        numriId: e.numriId,
        pershkrimi: e.pershkrimi,
        renditja: e.renditja
      });
    });

    return creationDTO;
  }

  getErrors(foljaForm: UntypedFormGroup) {
    let errors: {
      menyraId: number;
      kohaId: number;
      formaId: number;
      vetaId: number;
      numriId: number;
      pershkrimi: string;
    }[] = [];
    let initial = foljaForm.getRawValue().foljaKushtoreInitial;
    let formula = foljaForm.getRawValue().foljaKushtore;

    for (const fk of FoljaKushtore.FoljaKushtoreKoha) {
      for (const ff of FoljaConstants.FoljaForma) {
        for (const fv of FoljaConstants.FoljaVeta) {
          for (const fn of FoljaConstants.FoljaNumri) {
            const fmap = formula
              .filter((a: any) => a.kohaId == fk.id && a.formaId == ff.id && a.vetaId == fv.id && a.numriId == fn.id)
              .map((a: any) => a.pershkrimi)
              .join(', ');

            const imap = initial
              .filter((a: any) => a.kohaId == fk.id && a.formaId == ff.id && a.vetaId == fv.id && a.numriId == fn.id)
              .map((a: any) => a.pershkrimi)
              .join(', ');

            if (fmap != imap) {
              errors.push({
                menyraId: FoljaMenyra.Kushtore,
                kohaId: fk.id,
                formaId: ff.id,
                vetaId: fv.id,
                numriId: fn.id,
                pershkrimi: imap
              });
            }
          }
        }
      }
    }

    return errors;
  }
}
